import { motion, useInView, useAnimation } from "framer-motion";
import { useRef, useEffect, useState, useCallback } from "react";

/* ================================================================
   CORTES INDUSTRIES — ANIMATION PRIMITIVES
   Professional, restrained motion design.
   Inspired by: Palantir, Anduril, McKinsey digital.
   ================================================================ */

// ─── Fade Up on Scroll ────────────────────────────────────────────
export function FadeIn({
  children,
  delay = 0,
  duration = 0.7,
  y = 30,
  className = "",
  once = true,
  threshold = 0.15,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: threshold });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Fade In from side ────────────────────────────────────────────
export function FadeInX({
  children,
  delay = 0,
  duration = 0.7,
  x = -40,
  className = "",
  once = true,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: 0.15 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Stagger Children Container ──────────────────────────────────
export function StaggerContainer({
  children,
  stagger = 0.1,
  delay = 0,
  className = "",
  once = true,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: stagger,
            delayChildren: delay,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Stagger Child Item ──────────────────────────────────────────
export function StaggerItem({ children, className = "" }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 24 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
            ease: [0.25, 0.1, 0.25, 1],
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Reveal Line (gold accent) ───────────────────────────────────
export function RevealLine({ className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <motion.div
      ref={ref}
      className={`h-[2px] ${className}`}
      style={{
        background: "linear-gradient(90deg, transparent, hsl(41 47% 61%), transparent)",
      }}
      initial={{ scaleX: 0, opacity: 0 }}
      animate={
        isInView
          ? { scaleX: 1, opacity: 1 }
          : { scaleX: 0, opacity: 0 }
      }
      transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
    />
  );
}

// ─── Small Gold Bar ──────────────────────────────────────────────
export function GoldBar({ className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <motion.div
      ref={ref}
      className={`bg-[hsl(41,47%,61%)] ${className}`}
      initial={{ width: 0 }}
      animate={isInView ? { width: "3rem" } : { width: 0 }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
      style={{ height: 2 }}
    />
  );
}

// ─── Count-Up Number ─────────────────────────────────────────────
export function CountUp({ value, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!isInView) return;
    // If value is not a number, just display it
    const num = parseInt(value);
    if (isNaN(num)) {
      setDisplay(value);
      return;
    }
    let start = 0;
    const end = num;
    const stepTime = Math.max(duration * 1000 / end, 30);
    const timer = setInterval(() => {
      start += 1;
      setDisplay(String(start));
      if (start >= end) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [isInView, value, duration]);

  return <span ref={ref}>{display}{suffix}</span>;
}

// ─── Image Reveal (scale + clip) ─────────────────────────────────
export function ImageReveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <motion.div
      ref={ref}
      className={`overflow-hidden ${className}`}
      initial={{ opacity: 0, scale: 1.08 }}
      animate={
        isInView
          ? { opacity: 1, scale: 1 }
          : { opacity: 0, scale: 1.08 }
      }
      transition={{
        duration: 1,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {children}
    </motion.div>
  );
}

// ─── Page Wrapper (entry animation) ──────────────────────────────
export function PageTransition({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

// ─── Hover Card (lift + shadow) ──────────────────────────────────
export function HoverCard({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      whileHover={{
        y: -4,
        boxShadow: "0 12px 40px -8px rgba(0,0,0,0.12)",
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

// ─── Parallax Section Background ─────────────────────────────────
export function ParallaxBg({ children, className = "", speed = 0.15 }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const scrolled = window.innerHeight - rect.top;
    setOffset(scrolled * speed);
  }, [speed]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ y: offset }}>
        {children}
      </motion.div>
    </div>
  );
}
